/**
 * ONNX Runtime integration for face embedding and age estimation
 *
 * Uses WASM backend only — universal browser support, smallest bundle size.
 * WebGPU/WebGL backends were removed since:
 * - WebGPU has spotty mobile support (no Safari, partial Android Chrome)
 * - All inference code already used executionProviders: ['wasm']
 * - Saves ~308KB JS bundle + ~12MB WASM binary
 */

import * as ort from 'onnxruntime-web/wasm';
import { PreprocessedFace, preprocessFace } from './preprocessor';
import { BBox } from '../types';

export interface ONNXModels {
  embeddingModel: ort.InferenceSession | null;
  ageModel: ort.InferenceSession | null;
}

export class ONNXInference {
  private models: ONNXModels = {
    embeddingModel: null,
    ageModel: null,
  };
  private embeddingDim: number = 512;

  constructor(embeddingDim: number = 512) {
    this.embeddingDim = embeddingDim;
  }

  /**
   * Initialize ONNX runtime with WASM backend configuration
   */
  async initialize(): Promise<void> {
    ort.env.wasm.numThreads = navigator.hardwareConcurrency || 4;
    ort.env.wasm.simd = true;

    console.log('[ONNX] Backend: wasm');
  }

  /**
   * Load embedding model
   */
  async loadEmbeddingModel(modelPath: string): Promise<void> {
    try {
      this.models.embeddingModel = await ort.InferenceSession.create(modelPath, {
        executionProviders: ['wasm'],
      });

      console.log('[ONNX] Embedding model loaded');
    } catch (error) {
      console.error('Failed to load embedding model:', error);
      throw new Error(`Could not load embedding model from ${modelPath}`);
    }
  }

  /**
   * Load age estimation model
   */
  async loadAgeModel(modelPath: string): Promise<void> {
    try {
      this.models.ageModel = await ort.InferenceSession.create(modelPath, {
        executionProviders: ['wasm'],
      });
      console.log('[ONNX] Age model loaded successfully');
    } catch (error) {
      console.error('Failed to load age model:', error);
      throw new Error(`Could not load age model from ${modelPath}`);
    }
  }

  /**
   * Extract face embedding from preprocessed face
   */
  async extractEmbedding(
    preprocessed: PreprocessedFace,
    _imageWidth: number,
    _imageHeight: number
  ): Promise<number[]> {
    if (!this.models.embeddingModel) {
      throw new Error('Embedding model not loaded');
    }

    // Model expects input shape: [1, 3, 112, 112] (batch, channels, height, width)
    const inputShape = [1, 3, 112, 112];
    const inputTensor = new ort.Tensor('float32', preprocessed.normalized, inputShape);

    try {
      const feeds = { [this.models.embeddingModel.inputNames[0]]: inputTensor };
      const results = await this.models.embeddingModel.run(feeds);
      const outputName = this.models.embeddingModel.outputNames[0];
      const output = results[outputName];

      // Extract embedding vector
      const embedding = Array.from(output.data as Float32Array);

      // Normalize embedding (L2 normalization)
      const norm = Math.sqrt(embedding.reduce((sum, val) => sum + val * val, 0));
      const normalized = embedding.map((v) => v / norm);

      // Truncate or pad to embeddingDim
      if (normalized.length > this.embeddingDim) {
        return normalized.slice(0, this.embeddingDim);
      } else if (normalized.length < this.embeddingDim) {
        return [...normalized, ...new Array(this.embeddingDim - normalized.length).fill(0)];
      }

      return normalized;
    } catch (error) {
      console.error('Embedding extraction failed:', error);
      throw error;
    }
  }

  /**
   * Estimate age from face embedding
   */
  async estimateAge(embedding: number[]): Promise<{ age: number; confidence: number }> {
    if (!this.models.ageModel) {
      // Fallback: simple heuristic based on embedding (not accurate, but allows testing)
      console.warn('Age model not loaded, using fallback');
      return { age: 25.0, confidence: 0.5 };
    }

    try {
      // Age model input shape depends on model architecture
      // Common: [1, embedding_dim] or [1, 1, embedding_dim]
      const inputShape = [1, embedding.length];
      const inputTensor = new ort.Tensor('float32', new Float32Array(embedding), inputShape);

      const feeds = { [this.models.ageModel.inputNames[0]]: inputTensor };
      const results = await this.models.ageModel.run(feeds);
      const outputName = this.models.ageModel.outputNames[0];
      const output = results[outputName];

      // Output format depends on model: could be [age] or [age, confidence]
      const outputArray = Array.from(output.data as Float32Array);
      const age = outputArray[0];
      const confidence = outputArray.length > 1 ? outputArray[1] : 0.8;

      return {
        age: Math.max(0, Math.min(100, age)),
        confidence: Math.max(0, Math.min(1, confidence)),
      };
    } catch (error) {
      console.error('Age estimation failed:', error);
      // Fallback
      return { age: 25.0, confidence: 0.5 };
    }
  }

  /**
   * Process face image and return embedding
   */
  async processFace(
    image: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | ImageData,
    bbox: BBox
  ): Promise<number[]> {
    const preprocessed = preprocessFace(image, bbox);
    const imageWidth = image instanceof ImageData ? image.width : image.width || 640;
    const imageHeight = image instanceof ImageData ? image.height : image.height || 480;
    return this.extractEmbedding(preprocessed, imageWidth, imageHeight);
  }
}
